import { brand, neutral, semantic } from "@/constants/colors";
import { fonts } from "@/constants/fonts";
import type React from "react";
import type { ReactNode } from "react";
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  useColorScheme,
  View,
  type ViewStyle,
} from "react-native";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from "react-native-reanimated";
import ThemedText from "../core/ThemedText";

type FABSize = "sm" | "md" | "lg";
type FABColor = "primary" | "success" | "warning" | "error" | "neutral";
type FABPosition =
  | "bottom-right"
  | "bottom-left"
  | "top-right"
  | "top-left"
  | "bottom-center";

interface FABProps {
  onPress?: () => void;
  icon: ReactNode;
  label?: string;
  size?: FABSize;
  color?: FABColor;
  disabled?: boolean;
  loading?: boolean;
  style?: ViewStyle;
  position?: FABPosition;
  extended?: boolean; // Shows label alongside icon
}

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

export const FAB: React.FC<FABProps> = ({
  onPress,
  icon,
  label,
  size = "md",
  color = "primary",
  disabled = false,
  loading = false,
  style,
  position = "bottom-right",
  extended = false,
}) => {
  const colorScheme = useColorScheme();
  const isDark = colorScheme === "dark";
  const scale = useSharedValue(1);

  const sizeStyles: Record<FABSize, { size: number; fontSize: number }> = {
    sm: { size: 48, fontSize: 20 },
    md: { size: 56, fontSize: 24 },
    lg: { size: 64, fontSize: 28 },
  };

  const getButtonColor = () => {
    switch (color) {
      case "primary":
        return isDark ? brand.primary.dark : brand.primary.main;
      case "success":
        return isDark ? semantic.success.dark : semantic.success.main;
      case "warning":
        return semantic.warning.main;
      case "error":
        return isDark ? semantic.error.dark : semantic.error.main;
      case "neutral":
        return isDark ? neutral[600] : neutral[300];
    }
  };

  const isLightColor = (hexColor: string) => {
    const r = Number.parseInt(hexColor.slice(1, 3), 16);
    const g = Number.parseInt(hexColor.slice(3, 5), 16);
    const b = Number.parseInt(hexColor.slice(5, 7), 16);

    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5;
  };

  const getTextColor = () => {
    if (disabled) return isDark ? neutral[500] : neutral[400];

    const buttonColor = getButtonColor();

    if (color === "primary") {
      return "#FFFFFF";
    }
    return isLightColor(buttonColor) ? neutral[900] : "#FFFFFF";
  };

  const getPositionStyle = (): ViewStyle => {
    const offset = 20;
    switch (position) {
      case "bottom-right":
        return { position: "absolute", bottom: offset, right: offset };
      case "bottom-left":
        return { position: "absolute", bottom: offset, left: offset };
      case "top-right":
        return { position: "absolute", top: offset, right: offset };
      case "top-left":
        return { position: "absolute", top: offset, left: offset };
      case "bottom-center":
        return {
          position: "absolute",
          bottom: offset,
          left: "50%",
          transform: [{ translateX: -28 }], // Half of md size
        };
    }
  };

  const handlePressIn = () => {
    scale.value = withSpring(0.9, { damping: 15, stiffness: 400 });
  };

  const handlePressOut = () => {
    scale.value = withSpring(1, { damping: 15, stiffness: 400 });
  };

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));

  const buttonColor = getButtonColor();
  const textColor = getTextColor();

  const baseStyle: ViewStyle = {
    backgroundColor: buttonColor,
    width: extended ? undefined : sizeStyles[size].size,
    height: sizeStyles[size].size,
    opacity: disabled ? 0.5 : 1,
    paddingHorizontal: extended ? 20 : undefined,
  };

  return (
    <AnimatedPressable
      onPress={onPress}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      disabled={disabled || loading}
      style={[styles.fab, baseStyle, getPositionStyle(), animatedStyle, style]}
    >
      {loading ? (
        <ActivityIndicator color={textColor} size={sizeStyles[size].fontSize} />
      ) : (
        <View style={[styles.content, extended && styles.extendedContent]}>
          <View style={styles.iconContainer}>{icon}</View>
          {extended && label && (
            <ThemedText
              style={{
                color: textColor,
                fontSize: 16,
                fontFamily: fonts.semiBold,
                marginLeft: 8,
              }}
            >
              {label}
            </ThemedText>
          )}
        </View>
      )}
    </AnimatedPressable>
  );
};

// Mini FAB variant - commonly used in lists
interface MiniFABProps extends Omit<FABProps, "size" | "position" | "extended"> {
  style?: ViewStyle;
}

export const MiniFAB: React.FC<MiniFABProps> = (props) => {
  const miniStyle: ViewStyle = { position: "relative", ...(props.style as object) };
  return <FAB {...props} size="sm" style={miniStyle} />;
};

const styles = StyleSheet.create({
  fab: {
    borderRadius: 28,
    justifyContent: "center",
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 6,
  },
  content: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  extendedContent: {
    paddingHorizontal: 4,
  },
  iconContainer: {
    alignItems: "center",
    justifyContent: "center",
  },
});

export default FAB;
